import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Skeleton } from '@/components/shadcn/skeleton';
import communityService, {
  type CustomFormField,
  type MabarParticipant,
} from '@/services/communityService';
import badmintonService from '@/services/badmintonService';
import TambahGameForm from '@/components/community/badminton/TambahGameForm';
import BadmintonSubpageHeader from '@/components/community/badminton/BadmintonSubpageHeader';
import BadmintonConfirmDialog from '@/components/community/badminton/BadmintonConfirmDialog';
import BadmintonFeedbackDialog from '@/components/community/badminton/BadmintonFeedbackDialog';
import {
  buildGeneratePayload,
  createDefaultTambahGameForm,
  type TambahGameFormState,
} from '@/components/community/badminton/badmintonGenerateUtils';
import { hasIncompleteKotcSession } from '@/components/community/badminton/badmintonListUtils';
import { shouldConfirmLevelGenerate } from '@/components/community/badminton/badmintonLevelUtils';
import { hostPaths } from '@/utils/hostPaths';
import { clientPaths, isCustomerAppPath } from '@/utils/clientPaths';
import { getApiErrorMessage } from '@/utils/apiErrorMessage';
import { historyCanGoBack } from '@/utils/nativeNavigation';

type ConfirmKind = 'kotc' | 'level' | null;

export default function TambahBadmintonGamePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const isClient = isCustomerAppPath(useLocation().pathname);
  const [participants, setParticipants] = useState<MabarParticipant[]>([]);
  const [customForm, setCustomForm] = useState<CustomFormField[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [pendingForm, setPendingForm] = useState<TambahGameFormState | null>(null);
  const [confirmKind, setConfirmKind] = useState<ConfirmKind>(null);
  const [needKotcConfirm, setNeedKotcConfirm] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [courts, setCourts] = useState(createDefaultTambahGameForm().numCourts);
  const backPath =
    isClient && slug
      ? clientPaths.event.badminton(slug)
      : slug
        ? hostPaths.mabar.badminton(slug)
        : hostPaths.mabar.list;
  const goBack = () => {
    if (historyCanGoBack()) {
      navigate(-1);
      return;
    }
    navigate(backPath);
  };

  const hasLevelTemplate = useMemo(() => shouldConfirmLevelGenerate('random_rotation', customForm), [customForm]);

  useEffect(() => {
    if (!slug) return;
    Promise.all([
      communityService.getEventParticipants(slug, { status: 'confirmed', limit: 200 }),
      badmintonService.listSessions(slug),
      communityService.getEventBySlug(slug),
    ])
      .then(([partRes, sessions, event]) => {
        setParticipants(partRes.participants ?? []);
        setNeedKotcConfirm(hasIncompleteKotcSession(sessions));
        setCustomForm(event.custom_form ?? []);
      })
      .catch((err: unknown) => setErrorMessage(getApiErrorMessage(err, 'Gagal memuat pemain')))
      .finally(() => setLoading(false));
  }, [slug]);

  const submit = async (form: TambahGameFormState) => {
    if (!slug) return;
    setSubmitting(true);
    try {
      await badmintonService.generateSession(slug, buildGeneratePayload(form));
      navigate(backPath);
    } catch (err: unknown) {
      setErrorMessage(getApiErrorMessage(err, 'Gagal membuat jadwal'));
    } finally {
      setSubmitting(false);
      setPendingForm(null);
      setConfirmKind(null);
    }
  };

  const requestSubmit = (form: TambahGameFormState) => {
    if (form.rotationFormat === 'king_of_the_court' && needKotcConfirm) {
      setPendingForm(form);
      setConfirmKind('kotc');
      return;
    }
    if (shouldConfirmLevelGenerate(form.rotationFormat, customForm)) {
      setPendingForm(form);
      setConfirmKind('level');
      return;
    }
    void submit(form);
  };

  if (loading) {
    return (
      <div className="space-y-3 px-4 py-6">
        <Skeleton className="h-32 w-full rounded-2xl" />
        <Skeleton className="h-48 w-full rounded-2xl" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-4">
      <BadmintonSubpageHeader
        title="Tambah Game"
        subtitle={`${participants.length} pemain · ${courts} lapangan`}
        onBack={goBack}
      />
      <TambahGameForm
        confirmedCount={participants.length}
        participants={participants.map((p) => ({ id: p.id, name: p.name }))}
        submitting={submitting}
        onSubmit={requestSubmit}
        onFormChange={(form) => setCourts(form.numCourts)}
        onInsufficientClose={goBack}
      />
      <BadmintonConfirmDialog
        open={pendingForm != null && confirmKind === 'kotc'}
        title="King of the Court masih berjalan"
        message="Sesi KotC sebelumnya belum selesai. Tetap buat sesi baru?"
        confirmLabel="Buat Sesi Baru"
        onCancel={() => {
          setPendingForm(null);
          setConfirmKind(null);
        }}
        onConfirm={() => pendingForm && void submit(pendingForm)}
      />
      <BadmintonConfirmDialog
        open={pendingForm != null && confirmKind === 'level'}
        title="Konfirmasi level pemain"
        message={
          hasLevelTemplate
            ? 'Pastikan level tiap pemain sudah benar. Setelah generate, mengubah level memerlukan hapus semua jadwal badminton.'
            : 'Pastikan data pemain sudah benar sebelum generate.'
        }
        confirmLabel="Generate Jadwal"
        onCancel={() => {
          setPendingForm(null);
          setConfirmKind(null);
        }}
        onConfirm={() => pendingForm && void submit(pendingForm)}
      />
      <BadmintonFeedbackDialog
        open={Boolean(errorMessage)}
        icon="mdi:alert-circle-outline"
        title="Gagal membuat jadwal"
        message={errorMessage}
        onClose={() => setErrorMessage('')}
      />
    </div>
  );
}
